/**
 * The authenticated branch.
 *
 * Everything here is lazy through the route object's own `lazy`, not `React.lazy` plus a
 * `Suspense` per page: route-level lazy loads the component and its data together in one chunk
 * and hooks into the router's pending state, so a navigation shows the router's transition
 * rather than a Suspense fallback that blanks the screen it is replacing.
 *
 * The sale screen is the exception — it is where the app lands and where it lives, so it is in
 * the main bundle.
 */

import type { RouteObject } from 'react-router';

import { Permission } from '@pos/shared';

import { SalePage } from '@/pages/SalePage';

export const appRoutes: RouteObject[] = [
  {
    index: true,
    Component: SalePage,
    handle: { title: 'Sell', permission: Permission.SaleCreate },
  },

  {
    path: 'sell',
    Component: SalePage,
    handle: { title: 'Sell', permission: Permission.SaleCreate },
  },

  {
    path: 'payment',
    lazy: async () => ({ Component: (await import('@/pages/PaymentPage')).PaymentPage }),
    handle: { title: 'Payment', permission: Permission.SaleCreate },
  },

  {
    path: 'sales/:saleId/receipt',
    lazy: async () => ({ Component: (await import('@/pages/ReceiptPage')).ReceiptPage }),
    handle: { title: 'Receipt' },
  },

  {
    path: 'sales',
    lazy: async () => ({ Component: (await import('@/pages/MySalesPage')).MySalesPage }),
    handle: { title: 'My sales', permission: Permission.SaleViewOwn },
  },

  {
    path: 'sales/:saleId',
    lazy: async () => ({ Component: (await import('@/pages/SaleDetailPage')).SaleDetailPage }),
    handle: { title: 'Sale', permission: Permission.SaleViewOwn },
  },

  {
    path: 'shift',
    lazy: async () => ({ Component: (await import('@/pages/ShiftPage')).ShiftPage }),
    handle: { title: 'My shift', permission: Permission.ShiftViewOwn },
  },

  {
    path: 'profile',
    lazy: async () => ({ Component: (await import('@/pages/ProfilePage')).ProfilePage }),
    handle: { title: 'Profile' },
  },

  {
    path: 'find-unit',
    lazy: async () => ({ Component: (await import('@/pages/FindUnitPage')).FindUnitPage }),
    handle: { title: 'Find a unit', permission: Permission.UnitView },
  },

  {
    path: 'brokers',
    lazy: async () => ({ Component: (await import('@/pages/BrokerPhonesPage')).BrokerPhonesPage }),
    handle: { title: 'Phones with brokers', permission: Permission.BrokerView },
  },
];

/**
 * Prefetches a route's chunk. Called from the screen before it — the payment chunk warms while
 * the cart is open, so tapping Charge is instant rather than a download.
 */
export const prefetch = {
  payment: () => import('@/pages/PaymentPage'),
  receipt: () => import('@/pages/ReceiptPage'),
  sales: () => import('@/pages/MySalesPage'),
  shift: () => import('@/pages/ShiftPage'),
};
